import { useEffect, useRef, useState } from "react";
import type { ProductInfoCreative } from "@/feature/ads/cretives/types/creatives.types";
import { useAuth } from "@/context/AuthContext";
import { dictionary } from "@/utils/dictionary";
import type { Language } from "@/utils/dictionary";
import { Step1SelectProduct } from "./steps/Step1SelectProduct";
import { Step2AdStudio } from "./steps/Step2AdStudio";
import { CreateProductModal } from "./CreateProductModal";

type Step = 1 | 2;

export interface SpyAdReference {
  image_url: string;
  headline?: string;
  ad_copy?: string;
}

interface CreateAdWithIAModalProps {
  open: boolean;
  onClose: () => void;
  initialStep?: Step;
  initialProduct?: ProductInfoCreative | null;
  initialReference?: SpyAdReference;
  onGenerated?: () => void;
}

export function CreateAdWithIAModal({
  open,
  onClose,
  initialStep = 1,
  initialProduct = null,
  initialReference,
  onGenerated,
}: CreateAdWithIAModalProps) {
  const modalRef = useRef<HTMLDivElement | null>(null);
  const { language } = useAuth();
  const [step, setStep] = useState<Step>(initialStep);
  const [selectedProduct, setSelectedProduct] = useState<ProductInfoCreative | null>(
    initialProduct
  );
  const [showCreateProduct, setShowCreateProduct] = useState(false);

  useEffect(() => {
    if (!open) return;
setStep(initialStep);
    setSelectedProduct(initialProduct);
    setShowCreateProduct(false);
  }, [open, initialStep, initialProduct]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!modalRef.current?.contains(target)) {
        const isInsideNestedModal = target instanceof Element && target.closest("[data-fluxi-nested-modal]");
        if (!isInsideNestedModal) {
onClose();
        }
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      <div
        ref={modalRef}
        className={`relative bg-background rounded-2xl shadow-2xl overflow-hidden border border-border/50 ${step === 2 ? "w-full max-w-5xl h-[90vh] max-h-[860px]" : "w-full max-w-md"
          }`}
      >
        {step === 1 ? (
          <Step1SelectProduct
            language={language as Language}
            onClose={onClose}
            onSelectProduct={(product) => {
              setSelectedProduct(product);
              setStep(2);
            }}
            onCreateNew={() => {
              setShowCreateProduct(true);
            }}
          />
        ) : null}
        {step === 2 && selectedProduct ? (
          <Step2AdStudio
            product={selectedProduct}
            websiteId={selectedProduct.website_id}
            onClose={onClose}
            language={language as Language}
            initialReference={initialReference}
            onGenerated={onGenerated}
          />
        ) : null}
        {step === 2 && !selectedProduct && (
          <div className="p-8 text-center text-red-500 font-medium">
            {dictionary["step2-no-product-error"]?.[language as Language] || "Error: step=2 pero no hay producto seleccionado"}
          </div>
        )}
      </div>

      <CreateProductModal
        open={showCreateProduct}
        onClose={() => setShowCreateProduct(false)}
        onCreated={(newProduct) => {
          setShowCreateProduct(false);
          setSelectedProduct(newProduct);
          setStep(2);
        }}
        language={language as Language}
      />
    </div>
  );
}
